// @flow

import { Link as AceLink } from '@ansarada/ace-react';
import * as React from 'react';
import { track } from '../../core/lib/segment-actions';
import { Link, Button as MaceButton } from '../../internal/Button';
import PageContentBlock from '../../internal/PageContentBlock';
import PardotDialog from '../../internal/PardotDialog';
import Currency from './Currency';
import { CURRENCY, CURRENCY_SYMBOLS, formatCurrency } from './Currency/currencies';
import MaceLozenge from './Lozenge';
import staticProps from './props';
import styles from './styles.scss';
import type { PlanPriceDto, Price } from './types';
import { mapPlanPriceDtosToPrices } from './utils';

type PropsType = {
  enabledNewQuoteBuilderRedirect?: boolean,
  enabledCorporateAdvisorPromotion?: boolean,
};

type StaticPropsType = {
  lookingForTenderPricingText: string,
  findOutMoreText: string,
  findOutMoreUrl: string,
  getStartedTitle: string,
  getStartedDetails: string,
  getStartedDetails2: string,
  getPrices: () => Array<Price>,
};

const periods = ['MTM', 'Q3', 'S6', 'A'];

const dataSizeMap = {
  '250MB': {
    label: '250 MB',
  },
  '1GB': {
    label: '1 GB',
  },
  '2GB': {
    label: '2 GB',
  },
  '3GB': {
    label: '3GB',
  },
  '4GB': {
    label: '4 GB',
  },
  Unlimited: {
    label: 'More than 4 GB',
  },
};
const dataSizes = Object.keys(dataSizeMap);

const periodTexts = {
  MTM: {
    short: 'Monthly',
    long: 'per month',
  },
  Q3: {
    short: '3 months',
    long: 'per month for 3 months',
  },
  S6: {
    short: '6 months',
    long: 'per month for 6 months',
  },
  A: {
    short: '12 months',
    long: 'per month for 12 months',
  },
};

const pardotFormUrl = 'https://go.ansarada.com/landing-iframe.html?formid=1009';
const pardotFormTitle = 'Your details';

const overageLinkType = {
  Company: 'corporate',
  Advisors: 'advisor',
};

const DataPricing = ({
  enabledNewQuoteBuilderRedirect = false,
  enabledCorporateAdvisorPromotion = false,
}: PropsType) => {
  const {
    lookingForTenderPricingText,
    findOutMoreText,
    findOutMoreUrl,
    getStartedDetails2,
    getPrices,
  }: StaticPropsType = staticProps;

  const [userType, setUserType] = React.useState('Company');
  const [selectedDataSize, setSelectedDataSize] = React.useState('1GB');
  const [selectedPeriod, setSelectedPeriod] = React.useState('Q3');
  const [currency, setCurrency] = React.useState(CURRENCY.AUD);
  const [showingDialog, setShowingDialog] = React.useState(false);
  const [planPriceData, setPlanPriceData] = React.useState<PlanPriceDto[]>([]);

  React.useEffect(() => {
    const scriptElement = document.querySelector('#plan-prices-data');
    if (scriptElement) {
      const jsonData = scriptElement.textContent;
      if (jsonData) {
        const parsedData = JSON.parse(jsonData);
        setPlanPriceData(parsedData.PlanPrices);
      }
    }
  }, []);

  const priceGroups = React.useMemo(() => {
    return planPriceData.length > 0 ? mapPlanPriceDtosToPrices(planPriceData) : getPrices();
  }, [planPriceData, getPrices]);

  const onUserTypeSelected = value => {
    if (value === 'Advisors') {
      window.location.href = enabledNewQuoteBuilderRedirect
        ? `/quote`
        : `/advisors-pricing/${currency}`;
      return;
    }

    setUserType(value);
  };

  const onContactPlans = () => {
    setShowingDialog(true);
  };

  const priceItem = priceGroups.find(
    ({ lookupCode }) =>
      lookupCode ===
      `DLS-${selectedPeriod}-${
        selectedDataSize === '250MB' ? 'PL' : 'PR'
      }-${selectedDataSize}-${currency}`,
  );

  const getStartedTitle =
    userType === 'Company' ? 'Start preparing for free' : 'Advisors start preparing for free';
  const getStartedDetails =
    userType === 'Company'
      ? 'Prepare your deal for free. Your subscription begins when you invite your first guest.'
      : 'Free access to the full suite of advanced Ansarada features.';

  let actionButton = null;

  if (selectedDataSize === 'Unlimited') {
    actionButton = (
      <MaceButton className={styles.actionButton} onClick={onContactPlans}>
        Contact us
      </MaceButton>
    );
  } else if (userType === 'Company') {
    actionButton = (
      <Link
        href="/free-trial"
        className={styles.actionButton}
        analyticsEvent={{
          event: 'f_website_try-now-for-free.clicked',
          properties: {
            clicked_from: 'data_pricing',
          },
        }}
      >
        Start for FREE
      </Link>
    );
  } else {
    actionButton = (
      <Link
        href={`/quote?DataPlan=${dataSizeMap[selectedDataSize].label}&SubscriptionPlan=${
          periodTexts[selectedPeriod].short
        }`}
        className={styles.actionButton}
      >
        Get a quote and free data room
      </Link>
    );
  }

  const originalPrice = priceItem && priceItem.price ? Number(priceItem.price) : 0;
  const currencySymbol = CURRENCY_SYMBOLS[currency];
  const PeriodLozenge = enabledCorporateAdvisorPromotion ? MaceLozenge : MaceLozenge;

  const planPrice = (
    <>
      <span>
        {currencySymbol} {formatCurrency(originalPrice, currency)}
      </span>
      <div className={styles.currencyWrapper}>
        <Currency symbolVisible={false} currency={currency} onSelect={setCurrency} />
      </div>
    </>
  );

  return (
    <>
      {lookingForTenderPricingText && findOutMoreText && findOutMoreUrl && (
        <PageContentBlock className={styles.yellowToast}>
          <p>
            <span className={styles.yellowToastText}>{lookingForTenderPricingText}</span>
            <Link href={findOutMoreUrl} variant="BlackLink">
              {findOutMoreText}
            </Link>
          </p>
        </PageContentBlock>
      )}

      <PageContentBlock className={styles.contentBlock} background="FeaturedContrast">
        <h2 className={styles.title}>Who are you?</h2>
        <p className={styles.description}>Do you work for an Advisory firm or Company?</p>
        <MaceLozenge
          items={[{ key: 'Company', label: 'Company' }, { key: 'Advisors', label: 'Advisors' }]}
          selected={userType}
          onSelect={onUserTypeSelected}
          className={styles.buttonGroup}
        />
        <h1 className={styles.caption}>{getStartedTitle}</h1>
        <p className={styles.description}>
          {getStartedDetails} <br />
          {getStartedDetails2}
        </p>
        {userType === 'Advisors' && <div className={styles.tipOver} />}
      </PageContentBlock>

      {userType === 'Advisors' && (
        <div className={styles.tipWrapper}>
          <div className={styles.tip}>You only pay when your first guest user logs in</div>
        </div>
      )}

      <PageContentBlock className={styles.contentBlock}>
        <div>
          <h2 className={styles.subTitle}>What data plan best matches your needs?</h2>
          <p className={styles.description}>
            {'Small deals typically use 250 MB or less. '}
            <AceLink
              target="_blank"
              href={`/data-overages/${currency}?type=${overageLinkType[userType]}`}
              className={styles.dataOverageLink}
              onClick={() => {
                track('f_website_overages-pricing.clicked');
              }}
            >
              View costs for data overages
            </AceLink>
            .
          </p>
          <MaceLozenge
            items={dataSizes.map(item => ({ key: item, label: dataSizeMap[item].label }))}
            selected={selectedDataSize}
            onSelect={value => {
              setSelectedDataSize(value);
              track('f_website_data-plan-button.clicked', { data_plan: dataSizeMap[value].label });
            }}
            className={styles.buttonGroup}
          />
        </div>
        <div className={styles.durationSection}>
          <h2 className={styles.subTitle}>What duration best matches your needs?</h2>
          <p className={styles.description}>If you are unsure choose monthly.</p>
          <PeriodLozenge
            items={periods.map(item => ({ key: item, label: periodTexts[item].short }))}
            selected={selectedPeriod}
            onSelect={value => {
              setSelectedPeriod(value);
              track('f_website_subscription-plan-button.clicked', {
                subscription_plan: periodTexts[value].short,
              });
            }}
            className={styles.buttonGroup}
          />
        </div>
      </PageContentBlock>

      <PageContentBlock className={styles.contentBlock} background="Silver">
        {priceItem ? (
          <>
            <p className={styles.actionArea}>
              <p className={styles.price}>{planPrice}</p>
              <span data-testid="data-amount">
                {dataSizeMap[selectedDataSize].label} with unlimited users{' '}
              </span>
              <span data-testid="data-timeframe">{periodTexts[selectedPeriod].long} </span>
            </p>
            {currency === CURRENCY.AUD && (
              <p className={styles.taxDisclaimer}>Total amount excludes GST</p>
            )}
          </>
        ) : (
          <p className={styles.actionArea}>
            Reach out for a tailored price <br />
          </p>
        )}
        <div>{actionButton}</div>
      </PageContentBlock>

      {showingDialog && (
        <PardotDialog
          formTitle={pardotFormTitle}
          formUrl={pardotFormUrl}
          onToggle={() => setShowingDialog(false)}
        />
      )}
    </>
  );
};

export default DataPricing;
